import React, { createContext, useContext, useMemo } from "react";

import { useRouter } from "next/router";
import comparator from "string-comparison";

import { GUIDED_HIGHLIGHTING_SKILLS_META } from "@/view/GuidedHighlightingContainer/utils/GuidedHighlighting.constants";

import useAssignmentReducerContext from "../useAssignmentReducerContext";
import useGuidedHighlightingPost from "../useGuidedHighlightingPost/useGuidedHighlightingPost";
import { GUIDED_HIGHLIGHTING_QUESTION_STATUSES } from "../useGuidedHighlightingStudent/types";
import useGuidedHighlightingStudent from "../useGuidedHighlightingStudent/useGuidedHighlightingStudent";
import { IHandleHighlightingTextProps } from "./types";

interface IGuidedHighlightingStudentAnswerContext {
  handleHighlightingText: ({
    selectedText,
    startIndex,
    endIndex,
    articleId,
    isCorrect,
    status,
  }: {
    selectedText?: string | undefined;
    startIndex?: number | undefined;
    endIndex?: number | undefined;
    articleId?: string;
    isCorrect?: boolean | undefined;
    status?: GUIDED_HIGHLIGHTING_QUESTION_STATUSES;
  }) => void;
  handleCleanHighlighting: () => void;
  handleAttempt: () => void;
}

const SIMILARITY_THRESHOLD = 0.75;
const BASE_SCORE = 10;

const GuidedHighlightingStudentAnswerContext = createContext<
  IGuidedHighlightingStudentAnswerContext | undefined
>(undefined);

export const GuidedHighlightingStudentAnswerContextProvider = ({
  children,
}: React.PropsWithChildren) => {
  const [postAnswer] = useGuidedHighlightingPost();
  const {
    guidedHighlightingData,
    activeGuidedHighlightingQuestion,
    setActiveQuestion,
    handleSaveProgress,
    setGuidedHighlightingData,
  } = useGuidedHighlightingStudent();
  const router = useRouter();
  const {
    query: { levelId },
  } = router;
  const { updatedAssignment } = useAssignmentReducerContext();
  const finalAssignmentId = updatedAssignment?.content_id || "";

  const calculateSimilarity = (text1: string, text2: string) =>
    comparator.levenshtein.similarity(text1, text2);

  const isAnswerCorrect = (suggestedAnswer: string, selectedText: string) => {
    return (
      calculateSimilarity(suggestedAnswer, selectedText) >= SIMILARITY_THRESHOLD
    );
  };

  const calculateScore = (attempt: number) => Math.floor(BASE_SCORE / attempt);

  const handleHighlightingText = ({
    selectedText,
    startIndex,
    endIndex,
    articleId,
    isCorrect,
    status,
  }: IHandleHighlightingTextProps) => {
    if (activeGuidedHighlightingQuestion) {
      setActiveQuestion({
        ...activeGuidedHighlightingQuestion,
        selectedText,
        startIndex,
        endIndex,
        articleId: articleId || activeGuidedHighlightingQuestion.articleId,
        isCorrect:
          isCorrect === undefined
            ? isCorrect
            : activeGuidedHighlightingQuestion.isCorrect,
        status: status || GUIDED_HIGHLIGHTING_QUESTION_STATUSES.initial,
      });
    }
  };

  const handleCleanHighlighting = () =>
    handleHighlightingText({
      articleId: activeGuidedHighlightingQuestion?.articleId || "",
      status: GUIDED_HIGHLIGHTING_QUESTION_STATUSES.initial,
    });

  const handleAttempt = () => {
    if (!activeGuidedHighlightingQuestion || !guidedHighlightingData) return;

    const isCorrect = isAnswerCorrect(
      activeGuidedHighlightingQuestion.suggestedAnswer.content,
      activeGuidedHighlightingQuestion.selectedText || "",
    );

    const attempt = activeGuidedHighlightingQuestion?.attempt
      ? activeGuidedHighlightingQuestion.attempt + 1
      : 1;
    const tokensEarned = attempt < 2 || isCorrect ? calculateScore(attempt) : 0;
    const status =
      isCorrect || attempt > 1
        ? GUIDED_HIGHLIGHTING_QUESTION_STATUSES.completed
        : GUIDED_HIGHLIGHTING_QUESTION_STATUSES.tryAgain;
    const updatedQuestion = {
      ...activeGuidedHighlightingQuestion,
      attempt,
      isCorrect,
      tokensEarned,
      status,
    };

    const updatedData = {
      ...guidedHighlightingData,
      questions: guidedHighlightingData?.questions.map((question) =>
        question.id === updatedQuestion.id ? updatedQuestion : question,
      ),
    };

    setActiveQuestion(updatedQuestion);
    setGuidedHighlightingData(updatedData);

    const penName =
      GUIDED_HIGHLIGHTING_SKILLS_META[updatedQuestion.readingSkill].color;

    postAnswer({
      content_id: levelId,
      start_index: updatedQuestion.startIndex,
      end_index: updatedQuestion.endIndex,
      metadata: {
        pen_name: penName,
        text: updatedQuestion.selectedText || "",
        reading_skill: updatedQuestion.readingSkill || "",
        lexile_level: updatedQuestion.lexileLevel || "",
        is_correct: isCorrect,
        question_id: updatedQuestion.id,
        attempt_number: attempt,
        points: tokensEarned,
        assignment_id: finalAssignmentId || "",
        article_id: levelId || "",
      },
    });

    handleSaveProgress();
  };

  const contextValue = useMemo(
    () => ({
      handleHighlightingText,
      handleCleanHighlighting,
      handleAttempt,
    }),
    [activeGuidedHighlightingQuestion, guidedHighlightingData],
  );

  return (
    <GuidedHighlightingStudentAnswerContext.Provider value={contextValue}>
      {children}
    </GuidedHighlightingStudentAnswerContext.Provider>
  );
};

const useGuidedHighlightingStudentAnswer =
  (): IGuidedHighlightingStudentAnswerContext => {
    const context = useContext(GuidedHighlightingStudentAnswerContext);
    if (context === undefined) {
      throw new Error(
        "useGuidedHighlightingStudentAnswer must be used within a GuidedHighlightingStudentAnswerContextProvider.",
      );
    }
    return context;
  };

export default useGuidedHighlightingStudentAnswer;
